import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { htmlEncode } from '../../../utils/escape.utils';
import { MaintenanceWindowSimpleSchedulerSchema } from '../types';
import { GzDropdownWebComponent } from '../../../ui/gzDropdown';
import { GzFormFieldWebComponent } from '../../../ui/gzForm/formField';
import { RecurrenceOptions, WeekDays } from '../../../types/common/schedulerTypes';
import { FormGroup } from '../../../ui/gzForm/formGroup/form-group';
import { GzTimePickerWebComponent } from '../../../ui/gzTimePicker';
import { GzMultiSelectGroupWebComponent } from '../../../ui/gzMultiSelect';
import { RequiredValidator } from '../../../ui/gzForm/validators/required.validator';
import { MaxTimeValidator } from '../../../ui/gzForm/validators/max-time.validator';
import { MaintenanceWindowsConstants } from '../../MaintenanceWindowsConstants';

export class MaintenanceWindowSimpleSchedulerView extends BaseWebComponentView {
  public formGroup!: FormGroup;

  public dom: {
    $wrapper: HTMLElement;
    $schedulerTitle: HTMLElement;

    $recurrenceFormField: GzFormFieldWebComponent<GzDropdownWebComponent, string | number>;
    // Daily
    $recurrenceDailyOptionsContainer: HTMLElement;
    $recurrenceDailyAtFormField: GzFormFieldWebComponent<GzTimePickerWebComponent, any>;
    // Weekly
    $recurrenceWeeklyOptionsContainer: HTMLElement;
    $recurrenceWeeklyOnDaysFormField: GzFormFieldWebComponent<GzMultiSelectGroupWebComponent, Array<string | number>>;
    $recurrenceWeeklyAtFormField: GzFormFieldWebComponent<GzTimePickerWebComponent, any>;
  };
  getDomElements() {
    this.dom = {
      $wrapper: this.shadow.querySelector('.wrapper'),
      $schedulerTitle: this.shadow.querySelector('#scheduler-title'),
      $recurrenceFormField: this.shadow.querySelector('#recurrence-form-field'),
      // Daily
      $recurrenceDailyOptionsContainer: this.shadow.querySelector('#recurrence-daily-container'),
      $recurrenceDailyAtFormField: this.shadow.querySelector('#daily-at-form-field'),
      // Weekly
      $recurrenceWeeklyOptionsContainer: this.shadow.querySelector('#recurrence-weekly-container'),
      $recurrenceWeeklyOnDaysFormField: this.shadow.querySelector('#weekly-on-days-form-field'),
      $recurrenceWeeklyAtFormField: this.shadow.querySelector('#weekly-at-form-field'),
    };

    this.getTranslations();
    this.setTranslatedStrings();
  }

  getTranslations() {
    this.translations = this.stringsTranslator.getSchedulerStrings();
  }

  setTranslatedStrings() {
    this.dom.$recurrenceFormField.label = htmlEncode(this.dependencies.translateFn('Recurrence'));
    this.dom.$recurrenceDailyAtFormField.label = htmlEncode(this.dependencies.translateFn('At'));
    this.dom.$recurrenceWeeklyOnDaysFormField.label = htmlEncode(
      this.dependencies.translateFn('On the following days')
    );
    this.dom.$recurrenceWeeklyAtFormField.label = htmlEncode(this.dependencies.translateFn('At'));
  }

  buildSuccessView(data: {
    viewData: MaintenanceWindowSimpleSchedulerSchema;
    disabled: boolean;
    hasImmediatelyOption: boolean;
  }) {
    this.createFormGroup();

    this.populateRecurrenceDropdown(data.hasImmediatelyOption);
    this.populateDayPicker();

    this.setInitialDataInForm(data.viewData, data.hasImmediatelyOption);
    if (data.disabled) {
      this.setDisabledInnerComponents(data.disabled);
    }
  }

  setDisabledInnerComponents(disable: boolean) {
    for (const item of Object.keys(this.dom)) {
      if (this.dom[item] instanceof GzFormFieldWebComponent) {
        this.dom[item].disabled = disable;
      }
    }

    if (!disable) {
      // restore scheduler state
      this.dom.$recurrenceFormField.Input.dispatchEvent(
        new CustomEvent('triggerRevalidation', {
          detail: {},
          bubbles: true,
        })
      );
    }
  }

  private createFormGroup() {
    this.dom.$recurrenceWeeklyOnDaysFormField.Validators = [
      RequiredValidator(this.dependencies.translateFn('Select at least one day of the week.')),
    ];
    this.dom.$recurrenceDailyAtFormField.Validators = [
      MaxTimeValidator(
        MaintenanceWindowsConstants.MaintenanceWindowName.SchedulerMaxTime,
        this.dependencies.translateFn('Invalid start time. Select a different value.')
      ),
    ];
    this.dom.$recurrenceWeeklyAtFormField.Validators = [
      MaxTimeValidator(
        MaintenanceWindowsConstants.MaintenanceWindowName.SchedulerMaxTime,
        this.dependencies.translateFn('Invalid start time. Select a different value.')
      ),
    ];

    // create FormGroup
    this.formGroup = FormGroup.build({
      recurrence: this.dom.$recurrenceFormField,
      daily: {
        at: this.dom.$recurrenceDailyAtFormField,
      },
      weekly: {
        days: this.dom.$recurrenceWeeklyOnDaysFormField,
        at: this.dom.$recurrenceWeeklyAtFormField,
      },
    });
  }

  private setInitialDataInForm(viewData: MaintenanceWindowSimpleSchedulerSchema, hasImmediatelyOption: boolean) {
    this.formGroup.value = {
      recurrence:
        viewData?.recurrence ||
        (hasImmediatelyOption ? RecurrenceOptions.IMMEDIATELY.toString() : RecurrenceOptions.DAILY.toString()),
      daily: {
        at: viewData?.daily?.at || {
          hours: 21,
          minutes: 0,
        },
      },
      weekly: {
        days: viewData?.weekly?.days || [],
        at: viewData?.weekly?.at || {
          hours: 21,
          minutes: 0,
        },
      },
    };
  }

  public getFormGroup(): FormGroup {
    return this.formGroup;
  }

  populateRecurrenceDropdown(hasImmediatelyOption: boolean): void {
    const recurrenceOptions: Array<any> = [];
    if (hasImmediatelyOption) {
      recurrenceOptions.push({
        id: 0,
        title: this.translations.recurrence.IMMEDIATELY,
        value: String(RecurrenceOptions.IMMEDIATELY),
      });
    }
    recurrenceOptions.push(
      {
        id: 1,
        title: this.translations.recurrence.DAILY,
        value: String(RecurrenceOptions.DAILY),
      },
      {
        id: 2,
        title: this.translations.recurrence.WEEKLY,
        value: String(RecurrenceOptions.WEEKLY),
      }
    );

    this.dom.$recurrenceFormField.Input.setAttribute('options', JSON.stringify(recurrenceOptions));
  }

  populateDayPicker(): void {
    const daysArray = Object.keys(WeekDays).filter(x => isNaN(Number(x)));
    const options = Array.from(daysArray, element => ({
      label: this.translations.weekdays.shortNames[element],
      value: parseInt(WeekDays[element], 10),
    }));

    this.dom.$recurrenceWeeklyOnDaysFormField.Input.setAttribute('options', JSON.stringify(options));
  }

  setTitle(value: string) {
    this.dom.$schedulerTitle.innerText = value;
  }
}
